import React, { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const PW_KEY = "itf_admin_pw";

// One place — a single owner dashboard for bookings, donations, messages,
// New Leaf interest, Shadow Foundation applications, and general inquiries.
export default function AdminDashboard() {
  const [pw, setPw] = useState(() => sessionStorage.getItem(PW_KEY) || "");
  const [pwInput, setPwInput] = useState("");
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);
  const [section, setSection] = useState("bookings");

  // On mount: if we already have a password saved, try it silently.
  useEffect(() => {
    (async () => {
      if (pw) {
        const ok = await tryAuth(pw);
        setAuthed(ok);
        if (!ok) {
          sessionStorage.removeItem(PW_KEY);
          setPw("");
        }
      }
      setChecking(false);
    })();
    // eslint-disable-next-line
  }, []);

  const tryAuth = async (candidate) => {
    try {
      const r = await fetch(`${API}/api/admin/bookings`, {
        headers: { "X-Admin-Password": candidate },
      });
      return r.status !== 401;
    } catch {
      return false;
    }
  };

  const login = async (e) => {
    e.preventDefault();
    const ok = await tryAuth(pwInput);
    if (ok) {
      sessionStorage.setItem(PW_KEY, pwInput);
      setPw(pwInput);
      setAuthed(true);
    } else {
      toast.error("Wrong password");
    }
  };

  const logout = () => {
    sessionStorage.removeItem(PW_KEY);
    setPw("");
    setAuthed(false);
    setPwInput("");
  };

  if (checking) return <div className="min-h-screen bg-cream" />;

  if (!authed) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center px-6" data-testid="admin-login">
        <form onSubmit={login} className="w-full max-w-md bg-white border border-moss/15 p-10 shadow-sm">
          <p className="overline mb-3">Iron Talon</p>
          <h1 className="font-serif text-3xl text-moss font-light mb-6">Owner sign-in</h1>
          <input
            data-testid="admin-pw-input"
            type="password"
            required
            placeholder="Password"
            value={pwInput}
            onChange={(e) => setPwInput(e.target.value)}
            className="w-full border border-moss/25 px-4 py-3 text-coal focus:outline-none focus:border-moss"
            autoFocus
          />
          <button type="submit" className="btn-primary w-full mt-4" data-testid="admin-pw-submit">Sign in</button>
        </form>
      </div>
    );
  }

  const tabs = [
    { id: "bookings",     label: "Bookings" },
    { id: "past-guests",  label: "Past Guests" },
    { id: "calendar",     label: "Calendar" },
    { id: "people",       label: "People" },
    { id: "donations",    label: "Donations" },
    { id: "messages",     label: "Messages" },
    { id: "inquiries",    label: "Inquiries" },
    { id: "wellness",     label: "New Leaf" },
    { id: "foundation",   label: "Foundation" },
  ];

  return (
    <div className="min-h-screen bg-cream" data-testid="admin-dashboard">
      <header className="border-b border-moss/15 bg-white/80 backdrop-blur sticky top-0 z-10">
        <div className="container-x py-5 flex items-center justify-between">
          <div>
            <p className="overline text-clay">Owner Dashboard</p>
            <h1 className="font-serif text-2xl text-moss font-light">Iron Talon Farm</h1>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={async () => {
                const to = window.prompt("Send preview pre-arrival email to which address?", "irontalonfarm@gmail.com");
                if (!to) return;
                try {
                  const r = await fetch(`${API}/api/admin/preview/pre-arrival`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json", "X-Admin-Password": pw },
                    body: JSON.stringify({ to }),
                  });
                  const d = await r.json();
                  if (r.ok) toast.success(`Preview sent to ${d.to}`);
                  else toast.error(d.detail || "Preview failed");
                } catch { toast.error("Preview failed"); }
              }}
              className="text-xs text-coal/50 hover:text-moss uppercase tracking-widest"
              data-testid="admin-preview-pre-arrival"
            >Send pre-arrival preview</button>
            <button
              onClick={async () => {
                if (!window.confirm("Sweep all obvious test/fake records (any @example.com email, any name starting with 'test', or literal 'Approve Test' / 'Test Guest' / 'Jane Doe')? Real records are preserved. This cannot be undone.")) return;
                try {
                  const r = await fetch(`${API}/api/admin/cleanup-test-data`, {
                    method: "POST",
                    headers: { "X-Admin-Password": pw },
                  });
                  const d = await r.json();
                  if (r.ok) {
                    toast.success(`Removed ${d.total} test records`);
                    window.location.reload();
                  } else {
                    toast.error("Cleanup failed");
                  }
                } catch { toast.error("Cleanup failed"); }
              }}
              className="text-xs text-coal/50 hover:text-clay uppercase tracking-widest"
              data-testid="admin-cleanup-test"
            >Clear test data</button>
            <button onClick={logout} className="text-sm text-coal/60 hover:text-clay" data-testid="admin-logout">Sign out</button>
          </div>
        </div>
        <div className="container-x pb-3 flex gap-1 overflow-x-auto" data-testid="admin-tabs">
          {tabs.map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setSection(t.id)}
              className={`px-4 py-2 text-sm whitespace-nowrap transition-colors ${
                section === t.id
                  ? "bg-moss text-cream"
                  : "text-coal/70 hover:text-moss hover:bg-moss/5"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </header>

      <main className="container-x py-8">
        {section === "bookings"    && <Bookings pw={pw} />}
        {section === "past-guests" && <PastGuests pw={pw} />}
        {section === "calendar"    && <Calendar pw={pw} />}
        {section === "people"      && <People pw={pw} />}
        {section === "donations"   && <Donations pw={pw} />}
        {section === "messages"    && <Messages pw={pw} />}
        {section === "inquiries"   && <Inquiries pw={pw} />}
        {section === "wellness"    && <Wellness pw={pw} />}
        {section === "foundation"  && <Foundation pw={pw} />}
      </main>
    </div>
  );
}

// ---------- helpers ----------
async function apiGet(path, pw) {
  const r = await fetch(`${API}${path}`, { headers: { "X-Admin-Password": pw } });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return r.json();
}

async function apiPatch(path, body, pw) {
  const r = await fetch(`${API}${path}`, {
    method: "PATCH",
    headers: { "X-Admin-Password": pw, "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return r.json();
}

async function apiDelete(path, pw) {
  const r = await fetch(`${API}${path}`, {
    method: "DELETE",
    headers: { "X-Admin-Password": pw },
  });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return r.json();
}

// Delete a record after a hard confirm. Refuses to proceed on cancel.
async function confirmAndDelete({ kind, id, pw, label, onDone }) {
  if (!window.confirm(`Delete this ${label}? This cannot be undone.`)) return;
  try {
    await apiDelete(`/api/admin/${kind}/${id}`, pw);
    toast.success(`Deleted`);
    onDone?.();
  } catch (e) {
    toast.error("Delete failed");
  }
}

function DeleteBtn({ kind, id, pw, label, onDone }) {
  return (
    <button
      onClick={() => confirmAndDelete({ kind, id, pw, label, onDone })}
      className="text-[11px] text-coal/40 hover:text-clay uppercase tracking-widest"
      data-testid={`delete-${kind}-${id}`}
    >× Delete</button>
  );
}

function useList(fetcher) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const reload = async () => {
    setLoading(true);
    try {
      setRows(await fetcher());
    } catch (e) {
      setErr(String(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, []);
  return { rows, loading, err, reload };
}

const StatusPill = ({ status }) => {
  const map = {
    pending: "bg-clay/15 text-clay",
    approved: "bg-moss/15 text-moss",
    confirmed: "bg-moss/15 text-moss",
    declined: "bg-coal/10 text-coal/60",
    dismissed: "bg-coal/10 text-coal/60",
    new: "bg-clay/15 text-clay",
    cancelled: "bg-coal/10 text-coal/60",
  };
  return (
    <span className={`text-[11px] px-2 py-1 uppercase tracking-widest ${map[status] || "bg-coal/10 text-coal/60"}`}>
      {status || "—"}
    </span>
  );
};

const Row = ({ children, testid }) => (
  <div className="border border-moss/15 bg-white p-5 space-y-2 text-sm" data-testid={testid}>
    {children}
  </div>
);

const Empty = ({ label }) => (
  <p className="text-center text-coal/50 italic py-16" data-testid="admin-empty">{label}</p>
);

const fmtDate = (iso) => (iso || "").split("T")[0];

// ---------- Past Guests (approved reservations whose stay is complete) ----------
function PastGuests({ pw }) {
  const { rows, loading, reload } = useList(() =>
    apiGet("/api/admin/past-guests", pw).then((d) => d.guests || [])
  );
  const [q, setQ] = useState("");

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((g) =>
      (g.guest_name || "").toLowerCase().includes(s) ||
      (g.email || "").toLowerCase().includes(s) ||
      (g.guest_phone || "").toLowerCase().includes(s) ||
      (g.cottages || []).join(" ").toLowerCase().includes(s)
    );
  }, [rows, q]);

  const totalRevenue = rows.reduce((s, g) => s + (g.total_spent || 0), 0);
  const totalStays = rows.reduce((s, g) => s + (g.stay_count || 0), 0);

  const copyAllEmails = () => {
    const emails = filtered.map((g) => g.email).filter(Boolean).join(", ");
    navigator.clipboard.writeText(emails);
    toast.success(`Copied ${filtered.length} email${filtered.length === 1 ? "" : "s"} to clipboard`);
  };

  const exportCSV = () => {
    const rows = [
      ["Name", "Email", "Phone", "Stays", "Total spent", "Last cottage", "Last check-in", "Last check-out", "First stay"],
      ...filtered.map((g) => [
        g.guest_name, g.email, g.guest_phone,
        g.stay_count, `$${(g.total_spent || 0).toFixed(2)}`,
        g.last_cottage, g.last_stay_check_in, g.last_stay_check_out, g.first_stay_check_in,
      ]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${(c ?? "").toString().replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `past-guests-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
  };

  return (
    <div className="space-y-4" data-testid="section-past-guests">
      <p className="text-coal/60 text-sm italic">
        Every guest whose stay has already ended. Perfect for follow-up notes, review asks,
        referral requests, and future correspondence.
      </p>

      <div className="grid grid-cols-3 gap-4 py-4 border-y border-moss/15">
        <Stat label="Past guests" value={rows.length} />
        <Stat label="Total stays" value={totalStays} />
        <Stat label="Lifetime revenue" value={`$${totalRevenue.toFixed(2)}`} />
      </div>

      <div className="flex gap-2 flex-wrap items-center">
        <input
          type="search"
          placeholder="Search name, email, phone, cottage…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="flex-1 min-w-[220px] border border-moss/25 bg-white px-3 py-2 text-sm focus:outline-none focus:border-moss"
          data-testid="past-guests-search"
        />
        <button
          onClick={copyAllEmails}
          disabled={filtered.length === 0}
          className="text-xs uppercase tracking-widest border border-moss/40 text-moss px-3 py-2 hover:bg-moss/5 disabled:opacity-40"
          data-testid="past-guests-copy-emails"
        >Copy emails</button>
        <button
          onClick={exportCSV}
          disabled={filtered.length === 0}
          className="text-xs uppercase tracking-widest border border-moss/40 text-moss px-3 py-2 hover:bg-moss/5 disabled:opacity-40"
          data-testid="past-guests-export"
        >Export CSV</button>
      </div>

      {loading ? <p className="text-coal/50 italic py-8">Loading…</p> :
        filtered.length === 0 ? <Empty label={q ? "No matches." : "No past guests yet — check back after your first stay wraps up."} /> :
        filtered.map((g) => (
          <Row key={g.email} testid={`past-guest-${g.email}`}>
            <div className="flex justify-between items-start gap-4 flex-wrap">
              <div className="flex-1 min-w-0">
                <p className="font-medium text-coal">
                  {g.guest_name}
                  {g.stay_count > 1 && (
                    <span className="ml-2 text-[10px] uppercase tracking-widest bg-clay/10 text-clay px-2 py-0.5">
                      {g.stay_count}× guest
                    </span>
                  )}
                </p>
                <p className="text-coal/60 text-sm mt-1">
                  <a href={`mailto:${g.email}`} className="hover:text-moss underline">{g.email}</a>
                  {g.guest_phone && <span className="ml-3 text-coal/50">📞 {g.guest_phone}</span>}
                </p>
                <p className="text-coal/70 text-sm mt-2">
                  Last stayed at <strong>{g.last_cottage}</strong> · {g.last_stay_check_in} → {g.last_stay_check_out}
                </p>
                {g.cottages.length > 1 && (
                  <p className="text-coal/50 text-xs mt-1">Cottages stayed at: {g.cottages.join(", ")}</p>
                )}
                {g.first_stay_check_in && g.first_stay_check_in !== g.last_stay_check_in && (
                  <p className="text-coal/40 text-xs mt-1">Guest since {g.first_stay_check_in}</p>
                )}
              </div>
              <div className="text-right">
                <p className="text-moss font-medium">${(g.total_spent || 0).toFixed(2)}</p>
                <p className="text-coal/40 text-xs uppercase tracking-widest mt-1">Lifetime</p>
                <a
                  href={`mailto:${g.email}?subject=A%20quick%20hello%20from%20Iron%20Talon%20Farm`}
                  className="inline-block mt-3 text-xs uppercase tracking-widest border border-moss/40 text-moss px-3 py-1 hover:bg-moss hover:text-cream transition-colors"
                  data-testid={`past-guest-email-${g.email}`}
                >Send email</a>
              </div>
            </div>
          </Row>
        ))
      }
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="text-center">
      <p className="text-2xl font-serif text-moss">{value}</p>
      <p className="text-[10px] uppercase tracking-widest text-coal/50 mt-1">{label}</p>
    </div>
  );
}


// ---------- People (unified guest/contact directory) ----------
const ACTIVITY_META = {
  booking:               { label: "Booking",      color: "#3d5a3d" },
  inquiry:               { label: "Inquiry",      color: "#7a8a5c" },
  message:               { label: "Message",      color: "#8a6a4a" },
  donation:              { label: "Donation",     color: "#c9a14a" },
  foundation_application:{ label: "Foundation",   color: "#2b2b2b" },
  wellness_interest:     { label: "New Leaf",     color: "#5d7a8a" },
};

function People({ pw }) {
  const { rows, loading, reload } = useList(() => apiGet("/api/admin/people", pw).then((d) => d.people || []));
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState({});
  const [emailingTo, setEmailingTo] = useState(null); // { name, email }

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((p) =>
      (p.name || "").toLowerCase().includes(s) ||
      (p.email || "").toLowerCase().includes(s) ||
      (p.phone || "").toLowerCase().includes(s)
    );
  }, [rows, q]);

  return (
    <div className="space-y-4" data-testid="section-people">
      <div className="flex justify-between items-center gap-4 flex-wrap">
        <input
          data-testid="people-search"
          type="text"
          placeholder="Search by name, email, or phone…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="flex-1 min-w-[240px] border border-moss/25 px-4 py-2 text-coal focus:outline-none focus:border-moss bg-white"
        />
        <p className="text-xs text-coal/50">{filtered.length} of {rows.length} people</p>
      </div>

      {loading ? <p className="text-coal/50 italic py-8">Loading…</p> :
        filtered.length === 0 ? <Empty label={q ? "No matches." : "No people yet."} /> :
        filtered.map((p) => {
          const isOpen = !!expanded[p.email];
          return (
            <Row key={p.email} testid={`person-${p.email}`}>
              <div className="flex justify-between items-start gap-4 flex-wrap">
                <div className="flex-1 min-w-[240px]">
                  <p className="font-medium text-coal">{p.name || <em>unknown</em>}</p>
                  <p className="text-coal/70 text-xs mt-1">
                    <a href={`mailto:${p.email}`} className="hover:text-moss">{p.email}</a>
                    {p.phone && <> · <a href={`tel:${p.phone.replace(/\D/g,"")}`} className="hover:text-moss">{p.phone}</a></>}
                  </p>
                  <div className="flex gap-2 mt-2 flex-wrap">
                    {p.booking_count > 0 && (
                      <span className="text-[11px] px-2 py-0.5 bg-moss/10 text-moss uppercase tracking-widest">
                        {p.booking_count} approved stay{p.booking_count !== 1 ? "s" : ""}
                      </span>
                    )}
                    {p.donation_total > 0 && (
                      <span className="text-[11px] px-2 py-0.5 bg-clay/10 text-clay uppercase tracking-widest">
                        ${p.donation_total.toFixed(0)} donated
                      </span>
                    )}
                    <span className="text-[11px] px-2 py-0.5 bg-coal/5 text-coal/60 uppercase tracking-widest">
                      {p.activities.length} interaction{p.activities.length !== 1 ? "s" : ""}
                    </span>
                  </div>
                  <p className="text-coal/40 text-xs mt-2">
                    First seen {fmtDate(p.first_seen)} · Last seen {fmtDate(p.last_seen)}
                  </p>
                </div>
                <div className="flex flex-col gap-2 items-end">
                  <button
                    onClick={() => setEmailingTo({ name: p.name, email: p.email })}
                    data-testid={`send-email-${p.email}`}
                    className="text-xs px-3 py-1 bg-moss text-cream"
                  >Send email</button>
                  <button
                    onClick={() => setExpanded((s) => ({ ...s, [p.email]: !s[p.email] }))}
                    data-testid={`toggle-history-${p.email}`}
                    className="text-xs text-coal/60 hover:text-moss"
                  >{isOpen ? "Hide history ▲" : `Show history (${p.activities.length}) ▼`}</button>
                  <button
                    onClick={async () => {
                      if (!window.confirm(`Delete ${p.name || p.email} and ALL their records (bookings, messages, donations, etc.)? Cannot be undone.`)) return;
                      try {
                        const r = await fetch(`${API}/api/admin/people/${encodeURIComponent(p.email)}`, {
                          method: "DELETE",
                          headers: { "X-Admin-Password": pw },
                        });
                        if (!r.ok) throw new Error();
                        const d = await r.json();
                        toast.success(`Removed ${d.total} record${d.total !== 1 ? "s" : ""}`);
                        reload();
                      } catch { toast.error("Delete failed"); }
                    }}
                    data-testid={`delete-person-${p.email}`}
                    className="text-[11px] text-coal/40 hover:text-clay uppercase tracking-widest"
                  >× Delete person</button>
                </div>
              </div>

              {isOpen && (
                <div className="mt-4 border-t border-moss/10 pt-4 space-y-3" data-testid={`history-${p.email}`}>
                  {p.activities.map((a, i) => {
                    const meta = ACTIVITY_META[a.type] || { label: a.type, color: "#666" };
                    return (
                      <div key={i} className="flex gap-3 text-xs">
                        <span className="w-24 shrink-0" style={{ color: meta.color }}>{meta.label}</span>
                        <div className="flex-1">
                          <p className="text-coal/80">
                            {a.summary}
                            {a.status && <span className="ml-2 text-coal/50">· {a.status}</span>}
                          </p>
                          {a.notes && <p className="text-coal/60 italic mt-1 border-l-2 border-moss/15 pl-3">{a.notes}</p>}
                          <p className="text-coal/40 mt-1">{fmtDate(a.date)}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Row>
          );
        })
      }

      {emailingTo && (
        <SendEmailModal
          to={emailingTo}
          pw={pw}
          onClose={() => setEmailingTo(null)}
          onSent={() => { setEmailingTo(null); reload(); }}
        />
      )}
    </div>
  );
}

function SendEmailModal({ to, pw, onClose, onSent }) {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState(`Hi ${(to.name || "").split(" ")[0] || "there"},\n\n`);
  const [sending, setSending] = useState(false);

  const send = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const r = await fetch(`${API}/api/admin/send-email`, {
        method: "POST",
        headers: { "X-Admin-Password": pw, "Content-Type": "application/json" },
        body: JSON.stringify({ to: to.email, subject, body }),
      });
      if (!r.ok) throw new Error("Send failed");
      toast.success(`Email sent to ${to.email}`);
      onSent();
    } catch (err) {
      toast.error("Send failed. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-coal/60 backdrop-blur-sm z-50 flex items-center justify-center p-6" onClick={onClose} data-testid="send-email-modal">
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={send}
        className="bg-cream w-full max-w-2xl border border-moss/20 shadow-xl p-8 space-y-4"
      >
        <div>
          <p className="overline mb-1">Send Email</p>
          <h2 className="font-serif text-2xl text-moss font-light">To {to.name || to.email}</h2>
          <p className="text-coal/55 text-xs mt-1">From: hello@irontalonfarm.com → {to.email}</p>
        </div>

        <div>
          <label className="text-xs uppercase tracking-widest text-coal/60 block mb-1">Subject</label>
          <input
            data-testid="email-subject"
            type="text"
            required
            maxLength={200}
            placeholder="A note from Iron Talon Farm"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="w-full border border-moss/25 px-4 py-2 text-coal focus:outline-none focus:border-moss bg-white"
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-coal/60 block mb-1">Message</label>
          <textarea
            data-testid="email-body"
            required
            maxLength={10000}
            rows={12}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            className="w-full border border-moss/25 px-4 py-3 text-coal focus:outline-none focus:border-moss bg-white leading-relaxed"
          />
          <p className="text-coal/40 text-xs mt-1 italic">A closing "— Iron Talon Farm · irontalonfarm.com · 610-709-7341" is added automatically.</p>
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className="text-coal/60 text-sm px-4 py-2 hover:text-clay" data-testid="email-cancel">Cancel</button>
          <button type="submit" disabled={sending} className="btn-primary" data-testid="email-send">
            {sending ? "Sending…" : "Send"}
          </button>
        </div>
      </form>
    </div>
  );
}


// ---------- Calendar ----------
// A 3-month visual view of booked dates. Any date with an APPROVED reservation
// is highlighted; hover/tap shows which cottage(s) are booked. Filter by cottage.
const COTTAGE_COLORS = {
  "Hilltop Cottage":                 { bg: "#3d5a3d", label: "Hilltop"   },
  "Creekside Cottage":               { bg: "#7a8a5c", label: "Creekside" },
  "Cattail Cottage":                 { bg: "#8a6a4a", label: "Cattail"   },
  "Garden Cottage":                  { bg: "#a05a3a", label: "Garden"    },
  "The Granary":                     { bg: "#c9a14a", label: "Granary"   },
  "Loft Cottage":                    { bg: "#5d7a8a", label: "Loft"      },
  "Whole Farm (all 6 cottages)":     { bg: "#2b2b2b", label: "Whole Farm"},
  "More than one cottage":           { bg: "#2b2b2b", label: "Multi"     },
};

function daysInMonth(y, m) { return new Date(y, m + 1, 0).getDate(); }
function ymd(d) { return d.toISOString().split("T")[0]; }
function addDays(iso, n) { const d = new Date(iso + "T00:00:00Z"); d.setUTCDate(d.getUTCDate() + n); return ymd(d); }

function Calendar({ pw }) {
  const { rows, loading } = useList(() =>
    apiGet("/api/admin/bookings?status=approved", pw).then((d) => d.reservations || [])
  );
  const [blocks, setBlocks] = useState([]);
  const [blockSlug, setBlockSlug] = useState("all");
  const [blockStart, setBlockStart] = useState("");
  const [blockEnd, setBlockEnd] = useState("");
  const [blockNote, setBlockNote] = useState("");
  const [cottageFilter, setCottageFilter] = useState("all");
  const [monthOffset, setMonthOffset] = useState(0);

  const loadBlocks = async () => {
    try { const d = await apiGet("/api/admin/blocked-dates", pw); setBlocks(d.blocks || []); } catch {}
  };
  useEffect(() => { loadBlocks(); /* eslint-disable-next-line */ }, []);

  const addBlock = async (e) => {
    e.preventDefault();
    if (!blockStart || !blockEnd) return toast.error("Start and end dates required");
    if (blockEnd <= blockStart) return toast.error("End date must be after start");
    try {
      await fetch(`${API}/api/admin/blocked-dates`, {
        method: "POST",
        headers: { "X-Admin-Password": pw, "Content-Type": "application/json" },
        body: JSON.stringify({ cottage_slug: blockSlug, start_date: blockStart, end_date: blockEnd, note: blockNote || null }),
      });
      toast.success("Dates blocked");
      setBlockStart(""); setBlockEnd(""); setBlockNote("");
      loadBlocks();
    } catch { toast.error("Failed to block"); }
  };

  const removeBlock = async (id) => {
    if (!window.confirm("Un-block these dates?")) return;
    try {
      await fetch(`${API}/api/admin/blocked-dates/${id}`, { method: "DELETE", headers: { "X-Admin-Password": pw } });
      toast.success("Unblocked");
      loadBlocks();
    } catch { toast.error("Failed"); }
  };

  // Build a { "YYYY-MM-DD": [{cottage_name, guest_name}] } map from approved bookings + blocked dates.
  const bookedByDay = useMemo(() => {
    const map = {};
    for (const r of rows) {
      if (cottageFilter !== "all" && r.cottage_name !== cottageFilter) continue;
      let cursor = r.check_in;
      while (cursor && cursor < r.check_out) {
        if (!map[cursor]) map[cursor] = [];
        map[cursor].push({ cottage: r.cottage_name, guest: r.guest_name });
        cursor = addDays(cursor, 1);
      }
    }
    // Overlay owner-set blocks (show as "Blocked · note")
    for (const b of blocks) {
      const cName = b.cottage_slug === "all" ? "All cottages" : (b.cottage_slug.replace(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase()));
      if (cottageFilter !== "all" && cottageFilter !== cName && b.cottage_slug !== "all") continue;
      let cursor = b.start_date;
      while (cursor && cursor < b.end_date) {
        if (!map[cursor]) map[cursor] = [];
        map[cursor].push({ cottage: cName, guest: `Blocked${b.note ? ": " + b.note : ""}` });
        cursor = addDays(cursor, 1);
      }
    }
    return map;
  }, [rows, cottageFilter, blocks]);

  const now = new Date();
  const months = [0, 1, 2].map((k) => {
    const d = new Date(now.getFullYear(), now.getMonth() + monthOffset + k, 1);
    return { y: d.getFullYear(), m: d.getMonth() };
  });

  // Show a chip for every bookable cottage so blocks can be dropped in from either the form OR
  // the filter buttons -- even if a cottage has no direct bookings yet (e.g., filled on Airbnb).
  const cottageNames = [
    "Hilltop Cottage",
    "Creekside Cottage",
    "Cattail Cottage",
    "Garden Cottage",
    "The Granary",
    "Loft Cottage",
  ];
  const allSlugs = [
    { slug: "all", name: "All cottages" },
    { slug: "hilltop-cottage", name: "Hilltop" },
    { slug: "creekside-cottage", name: "Creekside" },
    { slug: "cattail-cottage", name: "Cattail" },
    { slug: "garden-cottage", name: "Garden" },
    { slug: "the-granary", name: "Granary" },
    { slug: "loft-cottage", name: "Loft" },
  ];

  return (
    <div data-testid="section-calendar" className="space-y-6">
      {/* Block dates form */}
      <div className="bg-white border border-moss/15 p-5" data-testid="block-form">
        <p className="overline mb-3">Block dates (maintenance, personal use, etc.)</p>
        <form onSubmit={addBlock} className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <select value={blockSlug} onChange={(e)=>setBlockSlug(e.target.value)} className="border border-moss/25 px-3 py-2 bg-white text-sm" data-testid="block-slug">
            {allSlugs.map(o => <option key={o.slug} value={o.slug}>{o.name}</option>)}
          </select>
          <input type="date" required value={blockStart} onChange={(e)=>setBlockStart(e.target.value)} className="border border-moss/25 px-3 py-2 bg-white text-sm" data-testid="block-start" />
          <input type="date" required value={blockEnd} onChange={(e)=>setBlockEnd(e.target.value)} className="border border-moss/25 px-3 py-2 bg-white text-sm" data-testid="block-end" />
          <input type="text" placeholder="Note (optional)" maxLength={200} value={blockNote} onChange={(e)=>setBlockNote(e.target.value)} className="border border-moss/25 px-3 py-2 bg-white text-sm" data-testid="block-note" />
          <button type="submit" className="btn-primary text-sm" data-testid="block-submit">Block</button>
        </form>
        {blocks.length > 0 && (
          <div className="mt-4 space-y-1 text-xs text-coal/70">
            {blocks.map(b => (
              <div key={b.id} className="flex items-center justify-between border-t border-moss/10 pt-2" data-testid={`block-${b.id}`}>
                <span>
                  <strong>{b.cottage_slug === "all" ? "All cottages" : b.cottage_slug}</strong>
                  : {b.start_date} → {b.end_date}
                  {b.note && <em className="text-coal/50"> · {b.note}</em>}
                </span>
                <button onClick={() => removeBlock(b.id)} className="text-clay hover:underline text-[11px]" data-testid={`unblock-${b.id}`}>× Unblock</button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Filter + navigation */}
      <div className="flex justify-between items-center gap-4 flex-wrap">
        <div className="flex gap-2 flex-wrap items-center">
          <span className="text-xs text-coal/60 uppercase tracking-widest mr-1">Filter:</span>
          <button
            onClick={() => setCottageFilter("all")}
            data-testid="cal-filter-all"
            className={`px-3 py-1 text-xs border ${cottageFilter === "all" ? "bg-moss text-cream border-moss" : "border-moss/20 text-coal/60 hover:border-moss"}`}
          >All</button>
          {cottageNames.map((n) => (
            <button
              key={n}
              onClick={() => setCottageFilter(n)}
              data-testid={`cal-filter-${n}`}
              className={`px-3 py-1 text-xs border ${cottageFilter === n ? "text-cream border-transparent" : "border-moss/20 text-coal/60 hover:border-moss"}`}
              style={cottageFilter === n ? { backgroundColor: (COTTAGE_COLORS[n]?.bg || "#3d5a3d") } : {}}
            >
              {COTTAGE_COLORS[n]?.label || n}
            </button>
          ))}
        </div>
        <div className="flex gap-2 items-center">
          <button onClick={() => setMonthOffset((n) => n - 1)} className="px-2 py-1 text-sm border border-moss/20 hover:border-moss" data-testid="cal-prev">← Prev</button>
          <button onClick={() => setMonthOffset(0)} className="px-3 py-1 text-xs uppercase tracking-widest border border-moss/20 hover:border-moss" data-testid="cal-today">Today</button>
          <button onClick={() => setMonthOffset((n) => n + 1)} className="px-2 py-1 text-sm border border-moss/20 hover:border-moss" data-testid="cal-next">Next →</button>
        </div>
      </div>

      {loading ? <p className="text-coal/50 italic py-8">Loading…</p> :
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {months.map(({ y, m }) => (
            <MonthGrid key={`${y}-${m}`} year={y} month={m} bookedByDay={bookedByDay} cottageFilter={cottageFilter} />
          ))}
        </div>
      }

      {rows.length === 0 && !loading && (
        <p className="text-coal/50 italic text-sm border-t border-moss/15 pt-4">
          No direct-booked stays yet — use the form above to block dates you're booked on Airbnb.
        </p>
      )}

      {rows.length > 0 && (
        <div className="text-xs text-coal/55 italic border-t border-moss/15 pt-4">
          Dates are highlighted per cottage. Overlapping colors on the same day = multiple cottages booked that night.
          A date turns fully solid only for the nights the guest actually stays (check-out day is not counted).
        </div>
      )}
    </div>
  );
}

function MonthGrid({ year, month, bookedByDay, cottageFilter }) {
  const monthName = new Date(year, month, 1).toLocaleString("en-US", { month: "long", year: "numeric" });
  const firstWeekday = new Date(year, month, 1).getDay(); // 0=Sun
  const days = daysInMonth(year, month);
  const cells = [];
  for (let i = 0; i < firstWeekday; i++) cells.push(null);
  for (let d = 1; d <= days; d++) {
    const iso = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    cells.push({ d, iso, bookings: bookedByDay[iso] || [] });
  }
  const today = new Date();
  const todayISO = ymd(new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate())));

  return (
    <div className="bg-white border border-moss/15" data-testid={`month-${year}-${month}`}>
      <div className="px-4 py-3 border-b border-moss/10 flex justify-between items-baseline">
        <p className="font-serif text-moss text-lg">{monthName}</p>
      </div>
      <div className="grid grid-cols-7 text-[10px] text-coal/40 uppercase tracking-widest px-2 pt-2">
        {["S","M","T","W","T","F","S"].map((d, i) => <div key={i} className="text-center py-1">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1 p-2">
        {cells.map((c, idx) => {
          if (!c) return <div key={idx} className="aspect-square" />;
          const isToday = c.iso === todayISO;
          const booked = c.bookings.length > 0;
          const primaryColor = booked ? (COTTAGE_COLORS[c.bookings[0].cottage]?.bg || "#3d5a3d") : "transparent";
          const title = booked
            ? c.bookings.map((b) => `${COTTAGE_COLORS[b.cottage]?.label || b.cottage}: ${b.guest}`).join("\n")
            : "Available";
          return (
            <div
              key={idx}
              title={title}
              data-testid={`day-${c.iso}`}
              className={`aspect-square text-xs flex flex-col items-center justify-center relative border ${
                booked ? "text-cream" : "text-coal/70 bg-cream/40"
              } ${isToday ? "ring-2 ring-clay" : "border-moss/10"}`}
              style={booked ? { backgroundColor: primaryColor, borderColor: primaryColor } : {}}
            >
              <span className="font-medium">{c.d}</span>
              {c.bookings.length > 1 && (
                <span className="absolute bottom-0.5 right-0.5 text-[9px] bg-white/25 px-1 rounded">×{c.bookings.length}</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}


// ---------- Bookings ----------
function Bookings({ pw }) {
  const [statusFilter, setStatusFilter] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const { rows: raw, loading, reload } = useList(() =>
    apiGet(`/api/admin/bookings${statusFilter ? `?status=${statusFilter}` : ""}`, pw).then((d) => d.reservations || [])
  );
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [statusFilter]);

  const rows = raw;

  const setStatus = async (id, newStatus) => {
    try {
      await apiPatch(`/api/admin/bookings/${id}`, { status: newStatus }, pw);
      toast.success(`Marked ${newStatus}`);
      reload();
    } catch (e) {
      toast.error("Update failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="section-bookings">
      <div className="flex justify-between items-center gap-2 flex-wrap">
        <div className="flex gap-2 flex-wrap">
          {["", "pending", "approved", "declined", "cancelled"].map((f) => (
            <button
              key={f || "all"}
              onClick={() => setStatusFilter(f)}
              data-testid={`filter-bookings-${f || "all"}`}
              className={`px-3 py-1 text-xs uppercase tracking-widest border ${
                statusFilter === f ? "bg-moss text-cream border-moss" : "border-moss/20 text-coal/60 hover:border-moss"
              }`}
            >
              {f || "all"}
            </button>
          ))}
        </div>
        <button
          onClick={() => setShowAdd((v) => !v)}
          className="text-xs uppercase tracking-widest px-3 py-1 border border-moss/40 text-moss hover:bg-moss hover:text-cream"
          data-testid="toggle-manual-booking"
        >
          {showAdd ? "× Cancel" : "+ Add manual reservation"}
        </button>
      </div>

      {showAdd && (
        <ManualBookingForm pw={pw} onDone={() => { setShowAdd(false); reload(); }} />
      )}

      {loading ? <p className="text-coal/50 italic py-8">Loading…</p> :
        rows.length === 0 ? <Empty label="No bookings yet." /> :
        rows.map((r) => (
          <Row key={r.id} testid={`booking-${r.id}`}>
            <div className="flex justify-between items-start gap-4 flex-wrap">
              <div>
                <p className="font-medium text-coal">{r.guest_name} <span className="text-coal/50 text-xs">· {r.guest_email}</span>{r.manual && <span className="ml-2 text-[10px] uppercase tracking-widest bg-moss/10 text-moss px-2 py-0.5">manual</span>}</p>
                <p className="text-coal/70 mt-1">
                  <strong>{r.cottage_name}</strong> · {r.check_in} → {r.check_out} · {r.nights} nights · {r.party_size} guests
                </p>
                {r.guest_phone && <p className="text-coal/50 text-xs mt-1">📞 {r.guest_phone}</p>}
                {r.message && <p className="text-coal/60 text-xs italic mt-2 border-l-2 border-moss/20 pl-3">{r.message}</p>}
                <p className="text-coal/40 text-xs mt-2">${(r.total || 0).toFixed(2)} · requested {fmtDate(r.created_at)}</p>
              </div>
              <div className="flex flex-col gap-2 items-end">
                <StatusPill status={r.status} />
                {r.status === "pending" && (
                  <div className="flex gap-2">
                    <button onClick={() => setStatus(r.id, "approved")} className="text-xs px-3 py-1 bg-moss text-cream" data-testid={`approve-${r.id}`}>Approve</button>
                    <button onClick={() => setStatus(r.id, "declined")} className="text-xs px-3 py-1 border border-clay/40 text-clay" data-testid={`decline-${r.id}`}>Decline</button>
                  </div>
                )}
                <DeleteBtn kind="booking" id={r.id} pw={pw} label="booking" onDone={reload} />
              </div>
            </div>
          </Row>
        ))
      }
    </div>
  );
}

const COTTAGE_SLUGS = [
  { slug: "hilltop-cottage",  name: "Hilltop Cottage" },
  { slug: "the-treehouse",    name: "The Treehouse" },
  { slug: "the-stone-cottage",name: "The Stone Cottage" },
  { slug: "the-farmhouse",    name: "The Farmhouse" },
  { slug: "whole-farm",       name: "Whole Farm" },
];

function ManualBookingForm({ pw, onDone }) {
  const [f, setF] = useState({
    cottage_slug: "hilltop-cottage",
    check_in: "",
    check_out: "",
    guest_name: "",
    guest_email: "",
    guest_phone: "",
    party_size: 2,
    total: "",
    status: "approved",
    note: "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...f, party_size: Number(f.party_size) };
      if (payload.total === "" || payload.total == null) delete payload.total;
      else payload.total = Number(payload.total);
      if (!payload.guest_phone) delete payload.guest_phone;
      if (!payload.note) delete payload.note;
      const r = await fetch(`${API}/api/admin/reservations/manual`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Admin-Password": pw },
        body: JSON.stringify(payload),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || `HTTP ${r.status}`);
      }
      toast.success("Reservation added");
      onDone?.();
    } catch (err) {
      toast.error(String(err.message || err));
    } finally {
      setSaving(false);
    }
  };

  const input = "w-full border border-moss/25 bg-white px-3 py-2 text-sm focus:outline-none focus:border-moss";

  return (
    <form onSubmit={submit} className="border border-moss/25 bg-moss/5 p-5 space-y-3" data-testid="manual-booking-form">
      <p className="overline text-moss">Add manual reservation</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="text-xs text-coal/70 space-y-1">Guest name
          <input required value={f.guest_name} onChange={set("guest_name")} className={input} data-testid="manual-guest-name" />
        </label>
        <label className="text-xs text-coal/70 space-y-1">Guest email
          <input required type="email" value={f.guest_email} onChange={set("guest_email")} className={input} data-testid="manual-guest-email" />
        </label>
        <label className="text-xs text-coal/70 space-y-1">Guest phone
          <input value={f.guest_phone} onChange={set("guest_phone")} className={input} data-testid="manual-guest-phone" />
        </label>
        <label className="text-xs text-coal/70 space-y-1">Cottage
          <select value={f.cottage_slug} onChange={set("cottage_slug")} className={input} data-testid="manual-cottage">
            {COTTAGE_SLUGS.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
          </select>
        </label>
        <label className="text-xs text-coal/70 space-y-1">Check in
          <input required type="date" value={f.check_in} onChange={set("check_in")} className={input} data-testid="manual-check-in" />
        </label>
        <label className="text-xs text-coal/70 space-y-1">Check out
          <input required type="date" value={f.check_out} onChange={set("check_out")} className={input} data-testid="manual-check-out" />
        </label>
        <label className="text-xs text-coal/70 space-y-1">Party size
          <input required type="number" min="1" max="64" value={f.party_size} onChange={set("party_size")} className={input} data-testid="manual-party-size" />
        </label>
        <label className="text-xs text-coal/70 space-y-1">Total ($)
          <input type="number" min="0" step="0.01" value={f.total} onChange={set("total")} className={input} data-testid="manual-total" />
        </label>
        <label className="text-xs text-coal/70 space-y-1">Status
          <select value={f.status} onChange={set("status")} className={input} data-testid="manual-status">
            <option value="approved">approved</option>
            <option value="pending">pending</option>
            <option value="declined">declined</option>
            <option value="cancelled">cancelled</option>
          </select>
        </label>
        <label className="text-xs text-coal/70 space-y-1 sm:col-span-2">Note (optional)
          <input value={f.note} onChange={set("note")} className={input} data-testid="manual-note" placeholder="e.g. Restored after accidental delete" />
        </label>
      </div>
      <button disabled={saving} type="submit" className="text-xs uppercase tracking-widest bg-moss text-cream px-4 py-2 disabled:opacity-50" data-testid="manual-booking-submit">
        {saving ? "Saving…" : "Save reservation"}
      </button>
    </form>
  );
}

// ---------- Donations ----------
function Donations({ pw }) {
  const { rows, loading } = useList(() => apiGet("/api/admin/donations", pw).then((d) => d.donations || []));
  const total = useMemo(() =>
    rows.filter((d) => d.status === "confirmed").reduce((s, d) => s + (d.amount_display || 0), 0),
    [rows]
  );
  return (
    <div className="space-y-4" data-testid="section-donations">
      {rows.length > 0 && (
        <div className="bg-moss text-cream p-6" data-testid="donations-summary">
          <p className="overline text-cream/60">Confirmed lifetime total</p>
          <p className="font-serif text-4xl font-light mt-1">${total.toFixed(2)}</p>
          <p className="text-cream/60 text-xs mt-1">{rows.filter((d) => d.status === "confirmed").length} confirmed · {rows.filter((d) => d.status === "pending").length} pending</p>
        </div>
      )}
      {loading ? <p className="text-coal/50 italic py-8">Loading…</p> :
        rows.length === 0 ? <Empty label="No donations yet." /> :
        rows.map((d) => (
          <Row key={d.id} testid={`donation-${d.id}`}>
            <div className="flex justify-between items-start gap-4 flex-wrap">
              <div>
                <p className="font-medium text-coal">
                  {d.amount_display ? `$${d.amount_display.toFixed(2)}` : "Custom amount"}
                </p>
                <p className="text-coal/70 text-xs mt-1">
                  {d.donor_name || "anonymous"}{d.donor_email ? ` · ${d.donor_email}` : ""}
                </p>
                {d.dedication && <p className="text-clay/80 text-xs italic mt-1">In honor of: {d.dedication}</p>}
                <p className="text-coal/40 text-xs mt-2">{fmtDate(d.created_at)}{d.receipt_sent_at ? ` · receipt sent ${fmtDate(d.receipt_sent_at)}` : ""}</p>
              </div>
              <div className="flex flex-col gap-2 items-end">
                <StatusPill status={d.status} />
                <DeleteBtn kind="donation" id={d.id} pw={pw} label="donation" onDone={() => window.location.reload()} />
              </div>
            </div>
          </Row>
        ))
      }
    </div>
  );
}

// ---------- Messages, Inquiries, Wellness, Foundation ----------
function Messages({ pw }) {
  const { rows, loading } = useList(() => apiGet("/api/admin/messages", pw).then((d) => d.messages || []));
  return (
    <div className="space-y-4" data-testid="section-messages">
      {loading ? <p className="text-coal/50 italic py-8">Loading…</p> :
        rows.length === 0 ? <Empty label="No contact messages yet." /> :
        rows.map((m) => (
          <Row key={m.id} testid={`message-${m.id}`}>
            <div className="flex justify-between items-start gap-4">
              <div className="flex-1">
                <p className="font-medium text-coal">{m.name} <span className="text-coal/50 text-xs">· {m.email}</span></p>
                {m.subject && <p className="text-coal/70 text-xs">Subject: {m.subject}</p>}
                <p className="text-coal/80 whitespace-pre-wrap mt-2">{m.message}</p>
                <p className="text-coal/40 text-xs mt-2">{fmtDate(m.created_at)}</p>
              </div>
              <DeleteBtn kind="message" id={m.id} pw={pw} label="message" onDone={() => window.location.reload()} />
            </div>
          </Row>
        ))
      }
    </div>
  );
}

function Inquiries({ pw }) {
  const { rows, loading } = useList(() => apiGet("/api/admin/reservation-inquiries", pw).then((d) => d.inquiries || []));
  return (
    <div className="space-y-4" data-testid="section-inquiries">
      {loading ? <p className="text-coal/50 italic py-8">Loading…</p> :
        rows.length === 0 ? <Empty label="No general inquiries yet." /> :
        rows.map((r) => (
          <Row key={r.id} testid={`inquiry-${r.id}`}>
            <div className="flex justify-between items-start gap-4">
              <div className="flex-1">
                <p className="font-medium text-coal">{r.name} <span className="text-coal/50 text-xs">· {r.email}{r.phone ? " · " + r.phone : ""}</span></p>
                <p className="text-coal/70 text-xs mt-1">
                  {r.lodging_interest || "No preference"}
                  {r.party_size ? ` · ${r.party_size} guests` : ""}
                  {r.arrival_date ? ` · ${r.arrival_date} → ${r.departure_date || "?"}` : ""}
                </p>
                {r.message && <p className="text-coal/80 whitespace-pre-wrap mt-2">{r.message}</p>}
                <p className="text-coal/40 text-xs mt-2">{fmtDate(r.created_at)}</p>
              </div>
              <DeleteBtn kind="inquiry" id={r.id} pw={pw} label="inquiry" onDone={() => window.location.reload()} />
            </div>
          </Row>
        ))
      }
    </div>
  );
}

function Wellness({ pw }) {
  const { rows, loading } = useList(() => apiGet("/api/admin/wellness", pw).then((d) => d.items || []));
  return (
    <div className="space-y-4" data-testid="section-wellness">
      {loading ? <p className="text-coal/50 italic py-8">Loading…</p> :
        rows.length === 0 ? <Empty label="No New Leaf interest yet." /> :
        rows.map((w) => (
          <Row key={w.id} testid={`wellness-${w.id}`}>
            <div className="flex justify-between items-start gap-4">
              <div className="flex-1">
                <p className="font-medium text-coal">{w.name} <span className="text-coal/50 text-xs">· {w.email}</span></p>
                {w.interest_area && <p className="text-coal/70 text-xs mt-1">Interest: {w.interest_area}</p>}
                {w.message && <p className="text-coal/80 whitespace-pre-wrap mt-2">{w.message}</p>}
                <p className="text-coal/40 text-xs mt-2">{fmtDate(w.created_at)}</p>
              </div>
              <DeleteBtn kind="wellness" id={w.id} pw={pw} label="entry" onDone={() => window.location.reload()} />
            </div>
          </Row>
        ))
      }
    </div>
  );
}

function Foundation({ pw }) {
  const { rows, loading } = useList(() => apiGet("/api/admin/foundation", pw).then((d) => d.applications || []));
  return (
    <div className="space-y-4" data-testid="section-foundation">
      {loading ? <p className="text-coal/50 italic py-8">Loading…</p> :
        rows.length === 0 ? <Empty label="No Shadow Foundation applications yet." /> :
        rows.map((f) => (
          <Row key={f.id} testid={`foundation-${f.id}`}>
            <div className="flex justify-between items-start gap-4">
              <div>
                <p className="font-medium text-coal">
                  {f.keep_anonymous ? <em>anonymous</em> : f.name} <span className="text-coal/50 text-xs">· {f.email}</span>
                </p>
                <p className="text-coal/70 text-xs mt-1">
                  {f.applicant_type || "—"}
                  {f.role ? ` · ${f.role}` : ""}
                  {f.branch ? ` · ${f.branch}` : ""}
                  {f.preferred_timing ? ` · ${f.preferred_timing}` : ""}
                </p>
                <p className="text-coal/80 whitespace-pre-wrap mt-2 italic">{f.season}</p>
                <p className="text-coal/40 text-xs mt-2">{fmtDate(f.created_at)}</p>
              </div>
              <div className="flex flex-col gap-2 items-end">
                <StatusPill status={f.status} />
                <DeleteBtn kind="foundation" id={f.id} pw={pw} label="application" onDone={() => window.location.reload()} />
              </div>
            </div>
          </Row>
        ))
      }
    </div>
  );
}
